import { useState } from "react";
import axios from "axios";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { User, Mail, Phone, Save } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerProfile = () => {
  const { user, getAuthHeaders, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      await axios.put(`${API}/users/${user.id}`, formData, {
        headers: getAuthHeaders(),
      });
      toast.success(t("profileUpdated"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failedToUpdateProfile"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 max-w-3xl py-8" data-testid="customer-profile">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">{t("profileSettings")}</h1>
          <p className="text-muted-foreground">{t("manageAccount")}</p>
        </div>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <User className="h-5 w-5" />
              {t("personalInfo")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="username" className="mb-2 block">{t("username")}</Label>
                <Input
                  id="username"
                  value={user?.username || ""}
                  disabled
                  className="h-12 bg-slate-100"
                />
                <p className="text-xs text-muted-foreground mt-1">{t("usernameCannotChange")}</p>
              </div>
              
              <div>
                <Label htmlFor="full_name" className="mb-2 block">{t("fullName")}</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder={t("yourFullName")}
                  className="h-12"
                  data-testid="profile-fullname"
                />
              </div>
              
              <div>
                <Label htmlFor="email" className="mb-2 block">{t("email")}</Label>
                <div className="relative">
                  <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground`} />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("yourEmail")}
                    className={`h-12 ${isRTL ? 'pr-10' : 'pl-10'}`}
                    data-testid="profile-email"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="phone" className="mb-2 block">{t("phone")}</Label>
                <div className="relative">
                  <Phone className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground`} />
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+218 xxx xxx xxx"
                    className={`h-12 ${isRTL ? 'pr-10' : 'pl-10'}`}
                    data-testid="profile-phone"
                  />
                </div>
              </div>
              
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="bg-[#1a365d] hover:bg-[#1a365d]/90"
                  data-testid="profile-save"
                >
                  <Save className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                  {isSaving ? t("saving") : t("saveChanges")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="border-0 shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="font-heading">{t("accountInfo")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b">
                <span className="text-muted-foreground">{t("accountType")}</span>
                <span className="font-medium capitalize">{user?.role}</span>
              </div>
              <div className="flex justify-between py-3 border-b">
                <span className="text-muted-foreground">{t("memberSince")}</span>
                <span className="font-medium">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString(isRTL ? 'ar-LY' : 'en-US') : "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-muted-foreground">{t("accountStatus")}</span>
                <span className="font-medium text-green-600">{t("active")}</span>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t">
              <Button variant="destructive" onClick={logout} data-testid="logout-btn">
                {t("signOut")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default CustomerProfile;
